import { authentication, useCreateDirectLink, useLogging } from '@ecosystem/newron-client-lib';
import { containers, feedbackV2, inputs } from '@ecosystem/newron-design-system';
import React, { useCallback, useEffect, useState } from 'react';

import { isTemplateAdmin } from '../../accessCheck';
import { useCollectionAnalytics } from '../../analytics';
import { MAX_DESCRIPTION_LENGTH, MAX_NAME_LENGTH, insightConstants } from '../../constants';
import { addInsightToCollections } from '../../services/collections.service';
import {
  AddInsightToCollectionsModalProps,
  CollectionType,
  CreateInsight,
  SelectedCollectionOption,
} from '../../types';
import { InsightModal, InsightModalMessage } from '../InsightModal';

const { Form } = containers;
const { Alert } = feedbackV2;
const { SAVE_SUCCESS_MESSAGE, ADD_INSIGHT_TO_COLLECTIONS_ERROR, ADD_INSIGHT_MODAL_TITLE } = insightConstants;

export function AddInsightToCollectionsModal<TInsightData extends object, TViewState extends object>({
  insightInfo,
  description,
  insightData,
  onSuccess,
  onClose,
  onError,
  isOpen,
  selectDataType,
  type,
  dataType,
  setDataType,
  proposition,
  getTrackingTags,
  createViewStateFn,
}: AddInsightToCollectionsModalProps<TInsightData, TViewState>): JSX.Element {
  const { error: logError } = useLogging();
  const { chartName } = insightInfo;
  const { RadioButtons } = inputs;

  const [selectedType, setSelectedType] = useState<CollectionType>(CollectionType.Collection);
  const [selectedCollections, setSelectedCollections] = useState<SelectedCollectionOption[]>([]);
  const [insightName, setInsightName] = useState<string>(chartName?.slice(0, MAX_NAME_LENGTH));
  const [insightDescription, setInsightDescription] = useState<string>(description?.slice(0, MAX_DESCRIPTION_LENGTH));
  const [selectedCollectionsValid, setSelectedCollectionsValid] = useState<boolean>();
  const [insightNameValid, setInsightNameValid] = useState<boolean>();

  const [message, setMessage] = useState<InsightModalMessage>();
  const [valid, setValid] = useState<boolean>(undefined);
  const [error, setError] = useState<string>(undefined);
  const { createLink } = useCreateDirectLink();

  const user = authentication.getUser();
  const showTemplates = isTemplateAdmin(user);

  const { trackInsightAddedToCollection } = useCollectionAnalytics();
  useEffect(() => {
    setValid(selectedCollectionsValid && insightNameValid);
  }, [selectedCollectionsValid, insightNameValid]);

  const clearMessage = useCallback(() => {
    setMessage(undefined);
  }, []);

  const handleClose = useCallback(() => {
    setInsightDescription(description?.slice(0, MAX_DESCRIPTION_LENGTH));
    setSelectedCollections([]);
    setInsightName(chartName?.slice(0, MAX_NAME_LENGTH));
    setError(undefined);
    onClose();
  }, [onClose, chartName, description]);

  const handleSave = useCallback(async () => {
    const insightPrototype: CreateInsight<TInsightData> = {
      name: insightName,
      dataType,
      description: insightDescription,
      proposition,
      type,
      data: insightData,
    };
    try {
      trackInsightAddedToCollection({
        insightName,
        selectedCollections,
        insightDescriptionAdded: !!insightDescription?.length,
        dataType,
        getTrackingTags,
        insightInfo,
      });

      if (createViewStateFn) {
        const viewLink = await createLink(createViewStateFn, { dataType });
        insightPrototype.viewKey = viewLink.viewKey;
      }

      await addInsightToCollections(insightPrototype, selectedCollections);
      setMessage({ type: 'success', message: SAVE_SUCCESS_MESSAGE(insightName, showTemplates) });
      onSuccess?.();
      handleClose();
    } catch (err) {
      logError(err, { action: 'Add insight to collection' });
      onError?.(err);
      throw new Error(ADD_INSIGHT_TO_COLLECTIONS_ERROR);
    }
  }, [
    insightName,
    dataType,
    insightDescription,
    proposition,
    type,
    insightData,
    createViewStateFn,
    trackInsightAddedToCollection,
    selectedCollections,
    getTrackingTags,
    onSuccess,
    handleClose,
    logError,
    onError,
    insightInfo,
    createLink,
    showTemplates,
  ]);

  return (
    <InsightModal
      isOpen={isOpen}
      message={message}
      onClose={handleClose}
      onError={onError}
      onMessageDismiss={clearMessage}
      onSave={handleSave}
      title={ADD_INSIGHT_MODAL_TITLE(showTemplates)}
      valid={!!valid}
      saveButtonText={ADD_INSIGHT_MODAL_TITLE(showTemplates)}
    >
      <Form>
        {showTemplates && (
          <RadioButtons
            name="AddToOptions"
            testId="add-to-options"
            title="Add to"
            options={[
              { label: 'Collection', value: CollectionType.Collection },
              { label: 'Template', value: CollectionType.Template },
            ]}
            value={selectedType}
            onChange={val => {
              setSelectedType(val as CollectionType);
            }}
            orientation="horizontal"
          />
        )}
        <InsightModal.CollectionNameInput
          selectedCollections={selectedCollections}
          setSelectedCollections={setSelectedCollections}
          setValid={setSelectedCollectionsValid}
          setError={setError}
          selectedType={selectedType}
        />
        <InsightModal.InsightNameInput name={insightName} setName={setInsightName} setValid={setInsightNameValid} />
        <InsightModal.InsightDescriptionInput description={insightDescription} setDescription={setInsightDescription} />
        {selectDataType && <InsightModal.CollectionStaticLiveInput dataType={dataType} setDataType={setDataType} />}
        {error && <Alert title={error} type="error" />}
      </Form>
    </InsightModal>
  );
}
